import { useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { utilities as csUtils } from '@cornerstonejs/core';

const useDownloadProgress = () => {
  const [progress, setProgress] = useState(null);
  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [connectionState, setConnectionState] = useState(signalR.HubConnectionState.Disconnected);
  const downloadIdRef = useRef(csUtils.uuidv4());
  const connectionRef = useRef(null);

  const startConnection = async () => {
    if (connectionRef.current) {
      return;
    }

    try {
      setConnectionState(signalR.HubConnectionState.Connecting);

      const connection = new signalR.HubConnectionBuilder()
        .withUrl('/api/downloadProgressHub', {
          // transport: signalR.HttpTransportType.ServerSentEvents,
          // signalR.HttpTransportType.WebSockets |
          // signalR.HttpTransportType.ServerSentEvents |
          // signalR.HttpTransportType.LongPolling,
        })
        .configureLogging(signalR.LogLevel.Information)
        .withAutomaticReconnect([0, 1000, 3000, 5000])
        .build();

      connection.on('ReceiveDownloadProgress', progress => {
        setProgress(progress);
      });

      connection.onreconnecting(error => {
        console.log('正在重连:', error.message);
        setConnected(false);
        setConnectionError(error);
        setConnectionState(signalR.HubConnectionState.Reconnecting);
      });

      connection.onreconnected(connectionId => {
        console.log('已重新连接，ID:', connectionId);
        setConnected(true);
        setConnectionError(null);
        setConnectionState(signalR.HubConnectionState.Connected);
        connection.invoke('JoinDownloadGroup', downloadIdRef.current);
      });

      connection.onclose(error => {
        console.log('连接已关闭:', error?.message);
        setConnected(false);
        setConnectionError(error);
        setConnectionState(signalR.HubConnectionState.Disconnected);
      });

      await connection.start();

      try {
        await connection.invoke('JoinDownloadGroup', downloadIdRef.current);
      } catch (groupErr) {
        console.error('加入下载组失败:', groupErr);
        setConnectionError(groupErr);
        throw groupErr; // 重新抛出错误，触发连接关闭
      }

      connectionRef.current = connection;
      setConnected(true);
      setConnectionError(null);
      setConnectionState(signalR.HubConnectionState.Connected);
      console.log('SignalR 连接成功');
    } catch (err) {
      console.error('SignalR 连接失败:', err);
      setConnectionError(err);
      setConnectionState(signalR.HubConnectionState.Disconnected);
    }
  };

  const stopConnection = async () => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.stop();
      } catch (err) {
        console.error('停止连接时出错:', err);
      } finally {
        connectionRef.current = null;
        setConnected(false);
        setConnectionError(null);
        setConnectionState(signalR.HubConnectionState.Disconnected);
      }
    }
  };

  const getDownloadUrl = (flag, studyUID, type, seriesUID?, instanceUID?) => {
    // const baseUrl = `/wadors/download`;
    const baseUrl = `/api/download`;
    const params = new URLSearchParams({ downloadId: downloadIdRef.current });

    switch (flag) {
      case 'studies':
        return `${baseUrl}/studies/${studyUID}/${type}?${params}`;
      case 'series':
        return `${baseUrl}/studies/${studyUID}/series/${seriesUID}/${type}?${params}`;
      case 'instances':
        return `${baseUrl}/studies/${studyUID}/series/${seriesUID}/instances/${instanceUID}/${type}?${params}`;
      default:
        throw new Error(`未知的下载类型: ${flag}`);
    }
  };

  const resetProgress = () => {
    setProgress({ count: 0, total: 0 });
  };

  useEffect(() => {
    startConnection();

    return () => {
      stopConnection();
    };
  }, []);

  return {
    progress,
    connected,
    connectionError,
    connectionState,
    startConnection,
    stopConnection,
    getDownloadUrl,
    downloadId: downloadIdRef.current,
    resetProgress,
  };
};

export default useDownloadProgress;
